import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { 
  Settings, 
  Save, 
  Bell, 
  Shield, 
  Users, 
  Building,
  Mail,
  Smartphone,
  Globe,
  Database,
  Key,
  AlertTriangle,
  CheckCircle,
  Info,
  Loader2,
  RefreshCw,
  Eye,
  EyeOff
} from 'lucide-react';

const SettingsPage = () => {
  const { user, hasPermission, getEmpresaId } = useAuth();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('geral');
  const [message, setMessage] = useState(null);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    if (hasPermission('manage_settings') || hasPermission('view_settings')) {
      loadSettings();
    }
  }, [hasPermission]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🔍 SettingsPage: Carregando configurações...');

      const empresaId = getEmpresaId();

      // Tentar carregar configurações do Firebase
      try {
        const getSettings = httpsCallable(functions, 'getSettings');
        const result = await getSettings({ id_empresa: empresaId });
        
        if (result.data && result.data.success) {
          setSettings(result.data.data);
          console.log('✅ SettingsPage: Configurações carregadas do Firebase');
        } else {
          throw new Error('Configurações não encontradas');
        }
      } catch (firebaseError) {
        console.log('⚠️ SettingsPage: Usando configurações padrão');
        // Usar configurações padrão se não existirem no Firebase
        setSettings(getDefaultSettings());
      }

      setLastUpdate(new Date());

    } catch (error) {
      console.error('❌ SettingsPage: Erro ao carregar configurações:', error);
      setError('Erro ao carregar configurações. Usando configurações padrão.');
      setSettings(getDefaultSettings());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultSettings = () => ({
    geral: {
      nome_empresa: user?.empresa?.nome || 'Empresa',
      idioma: 'pt-BR',
      fuso_horario: 'America/Sao_Paulo',
      formato_data: 'DD/MM/YYYY',
      formato_moeda: 'BRL',
      tema: 'light',
      logo_url: '',
      endereco: '',
      telefone: '',
      email_contato: ''
    },
    notificacoes: {
      email_novos_usuarios: true,
      email_documentos_vencendo: true,
      email_recebimentos: true,
      email_marketplace: true,
      email_fornecedores: true,
      sms_alertas_criticos: false,
      push_notifications: true,
      frequencia_relatorios: 'semanal',
      horario_relatorios: '08:00'
    },
    seguranca: {
      autenticacao_dois_fatores: false,
      sessao_timeout: 30,
      tentativas_login: 5,
      bloqueio_tempo: 15,
      senha_complexa: true,
      historico_senhas: 5,
      auditoria_logs: true,
      backup_automatico: true
    },
    sistema: {
      manutencao_modo: false,
      debug_logs: false,
      cache_timeout: 300,
      max_upload_size: 10,
      backup_frequencia: 'diario',
      backup_retencao: 30,
      api_rate_limit: 1000,
      webhook_url: ''
    },
    integracao: {
      api_externa_ativa: false,
      api_key: '',
      webhook_eventos: [],
      sincronizacao_automatica: false,
      timeout_requests: 30,
      retry_attempts: 3
    }
  });

  const saveSettings = async (section, data) => {
    if (!hasPermission('manage_settings')) {
      setError('Você não tem permissão para alterar configurações.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      
      console.log(`🔧 SettingsPage: Salvando configurações da seção: ${section}`, data);

      const empresaId = getEmpresaId();
      const updatedSettings = { ...settings, [section]: { ...settings[section], ...data } };

      try {
        const updateSettings = httpsCallable(functions, 'updateSettings');
        await updateSettings({
          id_empresa: empresaId,
          secao: section,
          configuracoes: data
        });
        
        console.log('✅ SettingsPage: Configurações salvas no Firebase');
      } catch (firebaseError) {
        console.log('⚠️ SettingsPage: Salvando localmente (Firebase indisponível)');
        // Continuar com salvamento local se Firebase falhar
      }

      setSettings(updatedSettings);
      setMessage({ type: 'success', text: 'Configurações salvas com sucesso!' });
      setLastUpdate(new Date());
      
      // Limpar mensagem após 3 segundos
      setTimeout(() => setMessage(null), 3000);
      
    } catch (error) {
      console.error('❌ SettingsPage: Erro ao salvar configurações:', error);
      setError('Erro ao salvar configurações. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = () => {
    loadSettings();
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setMessage(null);
    setError('');
  };

  const handleInputChange = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSaveSection = () => {
    saveSettings(activeTab, settings[activeTab]);
  };

  // Verificar permissão
  if (!hasPermission('manage_settings') && !hasPermission('view_settings')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Acesso Negado</h3>
          <p className="text-gray-600">Você não tem permissão para visualizar configurações.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Carregando configurações...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'geral', label: 'Geral', icon: Settings },
    { id: 'notificacoes', label: 'Notificações', icon: Bell },
    { id: 'seguranca', label: 'Segurança', icon: Shield },
    { id: 'sistema', label: 'Sistema', icon: Database },
    { id: 'integracao', label: 'Integração', icon: Globe }
  ];

  const renderGeralTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Nome da Empresa</label>
          <input
            type="text"
            value={settings.geral?.nome_empresa || ''}
            onChange={(e) => handleInputChange('geral', 'nome_empresa', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!hasPermission('manage_settings')}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Email de Contato</label>
          <input
            type="email"
            value={settings.geral?.email_contato || ''}
            onChange={(e) => handleInputChange('geral', 'email_contato', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!hasPermission('manage_settings')}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Telefone</label>
          <input
            type="tel"
            value={settings.geral?.telefone || ''}
            onChange={(e) => handleInputChange('geral', 'telefone', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!hasPermission('manage_settings')}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Idioma</label>
          <select
            value={settings.geral?.idioma || 'pt-BR'}
            onChange={(e) => handleInputChange('geral', 'idioma', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!hasPermission('manage_settings')}
          >
            <option value="pt-BR">Português (Brasil)</option>
            <option value="en-US">English (US)</option>
            <option value="es-ES">Español</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Fuso Horário</label>
          <select
            value={settings.geral?.fuso_horario || 'America/Sao_Paulo'}
            onChange={(e) => handleInputChange('geral', 'fuso_horario', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!hasPermission('manage_settings')}
          >
            <option value="America/Sao_Paulo">São Paulo (GMT-3)</option>
            <option value="America/New_York">New York (GMT-5)</option>
            <option value="Europe/London">London (GMT+0)</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Tema</label>
          <select
            value={settings.geral?.tema || 'light'}
            onChange={(e) => handleInputChange('geral', 'tema', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!hasPermission('manage_settings')}
          >
            <option value="light">Claro</option>
            <option value="dark">Escuro</option>
            <option value="auto">Automático</option>
          </select>
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Endereço</label>
        <textarea
          value={settings.geral?.endereco || ''}
          onChange={(e) => handleInputChange('geral', 'endereco', e.target.value)}
          rows={3}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={!hasPermission('manage_settings')}
        />
      </div>
    </div>
  );

  const renderNotificacoesTab = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <h4 className="text-lg font-medium text-gray-900">Notificações por Email</h4>
        
        {[
          { key: 'email_novos_usuarios', label: 'Novos usuários cadastrados' },
          { key: 'email_documentos_vencendo', label: 'Documentos próximos ao vencimento' },
          { key: 'email_recebimentos', label: 'Novos recebimentos' },
          { key: 'email_marketplace', label: 'Atividades do marketplace' },
          { key: 'email_fornecedores', label: 'Novos fornecedores' }
        ].map((item) => (
          <div key={item.key} className="flex items-center justify-between">
            <span className="text-sm text-gray-700">{item.label}</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.notificacoes?.[item.key] || false}
                onChange={(e) => handleInputChange('notificacoes', item.key, e.target.checked)}
                className="sr-only peer"
                disabled={!hasPermission('manage_settings')}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        ))}
      </div>
      
      <div className="space-y-4">
        <h4 className="text-lg font-medium text-gray-900">Outras Notificações</h4>
        
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-700">SMS para alertas críticos</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.notificacoes?.sms_alertas_criticos || false}
              onChange={(e) => handleInputChange('notificacoes', 'sms_alertas_criticos', e.target.checked)}
              className="sr-only peer"
              disabled={!hasPermission('manage_settings')}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-700">Push notifications</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.notificacoes?.push_notifications || false}
              onChange={(e) => handleInputChange('notificacoes', 'push_notifications', e.target.checked)}
              className="sr-only peer"
              disabled={!hasPermission('manage_settings')}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Frequência de Relatórios</label>
          <select
            value={settings.notificacoes?.frequencia_relatorios || 'semanal'}
            onChange={(e) => handleInputChange('notificacoes', 'frequencia_relatorios', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!hasPermission('manage_settings')}
          >
            <option value="diario">Diário</option>
            <option value="semanal">Semanal</option>
            <option value="mensal">Mensal</option>
            <option value="nunca">Nunca</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Horário dos Relatórios</label>
          <input
            type="time"
            value={settings.notificacoes?.horario_relatorios || '08:00'}
            onChange={(e) => handleInputChange('notificacoes', 'horario_relatorios', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!hasPermission('manage_settings')}
          />
        </div>
      </div>
    </div>
  );

  const renderSegurancaTab = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <h4 className="text-lg font-medium text-gray-900">Autenticação</h4>
        
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm font-medium text-gray-700">Autenticação de dois fatores</span>
            <p className="text-xs text-gray-500">Adiciona uma camada extra de segurança</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.seguranca?.autenticacao_dois_fatores || false}
              onChange={(e) => handleInputChange('seguranca', 'autenticacao_dois_fatores', e.target.checked)}
              className="sr-only peer"
              disabled={!hasPermission('manage_settings')}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm font-medium text-gray-700">Senhas complexas obrigatórias</span>
            <p className="text-xs text-gray-500">Exige senhas com maiúsculas, números e símbolos</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.seguranca?.senha_complexa || false}
              onChange={(e) => handleInputChange('seguranca', 'senha_complexa', e.target.checked)}
              className="sr-only peer"
              disabled={!hasPermission('manage_settings')}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Timeout de Sessão (minutos)</label>
          <input
            type="number"
            min="5"
            max="480"
            value={settings.seguranca?.sessao_timeout || 30}
            onChange={(e) => handleInputChange('seguranca', 'sessao_timeout', parseInt(e.target.value))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!hasPermission('manage_settings')}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Tentativas de Login</label>
          <input
            type="number"
            min="3"
            max="10"
            value={settings.seguranca?.tentativas_login || 5}
            onChange={(e) => handleInputChange('seguranca', 'tentativas_login', parseInt(e.target.value))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!hasPermission('manage_settings')}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Tempo de Bloqueio (minutos)</label>
          <input
            type="number"
            min="5"
            max="60"
            value={settings.seguranca?.bloqueio_tempo || 15}
            onChange={(e) => handleInputChange('seguranca', 'bloqueio_tempo', parseInt(e.target.value))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!hasPermission('manage_settings')}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Histórico de Senhas</label>
          <input
            type="number"
            min="0"
            max="10"
            value={settings.seguranca?.historico_senhas || 5}
            onChange={(e) => handleInputChange('seguranca', 'historico_senhas', parseInt(e.target.value))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!hasPermission('manage_settings')}
          />
        </div>
      </div>
      
      <div className="space-y-4">
        <h4 className="text-lg font-medium text-gray-900">Auditoria e Backup</h4>
        
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-700">Logs de auditoria</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.seguranca?.auditoria_logs || false}
              onChange={(e) => handleInputChange('seguranca', 'auditoria_logs', e.target.checked)}
              className="sr-only peer"
              disabled={!hasPermission('manage_settings')}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-700">Backup automático</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.seguranca?.backup_automatico || false}
              onChange={(e) => handleInputChange('seguranca', 'backup_automatico', e.target.checked)}
              className="sr-only peer"
              disabled={!hasPermission('manage_settings')}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>
    </div>
  );

  const renderSistemaTab = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <h4 className="text-lg font-medium text-gray-900">Configurações do Sistema</h4>
        
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm font-medium text-gray-700">Modo de manutenção</span>
            <p className="text-xs text-gray-500">Bloqueia acesso ao sistema para manutenção</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.sistema?.manutencao_modo || false}
              onChange={(e) => handleInputChange('sistema', 'manutencao_modo', e.target.checked)}
              className="sr-only peer"
              disabled={!hasPermission('manage_settings')}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-700">Logs de debug</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.sistema?.debug_logs || false}
              onChange={(e) => handleInputChange('sistema', 'debug_logs', e.target.checked)}
              className="sr-only peer"
              disabled={!hasPermission('manage_settings')}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Cache Timeout (segundos)</label>
          <input
            type="number"
            min="60"
            max="3600"
            value={settings.sistema?.cache_timeout || 300}
            onChange={(e) => handleInputChange('sistema', 'cache_timeout', parseInt(e.target.value))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!hasPermission('manage_settings')}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Tamanho Máximo Upload (MB)</label>
          <input
            type="number"
            min="1"
            max="100"
            value={settings.sistema?.max_upload_size || 10}
            onChange={(e) => handleInputChange('sistema', 'max_upload_size', parseInt(e.target.value))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!hasPermission('manage_settings')}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Frequência de Backup</label>
          <select
            value={settings.sistema?.backup_frequencia || 'diario'}
            onChange={(e) => handleInputChange('sistema', 'backup_frequencia', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!hasPermission('manage_settings')}
          >
            <option value="diario">Diário</option>
            <option value="semanal">Semanal</option>
            <option value="mensal">Mensal</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Retenção de Backup (dias)</label>
          <input
            type="number"
            min="7"
            max="365"
            value={settings.sistema?.backup_retencao || 30}
            onChange={(e) => handleInputChange('sistema', 'backup_retencao', parseInt(e.target.value))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!hasPermission('manage_settings')}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Rate Limit API (req/hora)</label>
          <input
            type="number"
            min="100"
            max="10000"
            value={settings.sistema?.api_rate_limit || 1000}
            onChange={(e) => handleInputChange('sistema', 'api_rate_limit', parseInt(e.target.value))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!hasPermission('manage_settings')}
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Webhook URL</label>
        <input
          type="url"
          value={settings.sistema?.webhook_url || ''}
          onChange={(e) => handleInputChange('sistema', 'webhook_url', e.target.value)}
          placeholder="https://exemplo.com/webhook"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={!hasPermission('manage_settings')}
        />
      </div>
    </div>
  );

  const renderIntegracaoTab = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <h4 className="text-lg font-medium text-gray-900">API Externa</h4>
        
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm font-medium text-gray-700">API externa ativa</span>
            <p className="text-xs text-gray-500">Permite integração com sistemas externos</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.integracao?.api_externa_ativa || false}
              onChange={(e) => handleInputChange('integracao', 'api_externa_ativa', e.target.checked)}
              className="sr-only peer"
              disabled={!hasPermission('manage_settings')}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-700">Sincronização automática</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.integracao?.sincronizacao_automatica || false}
              onChange={(e) => handleInputChange('integracao', 'sincronizacao_automatica', e.target.checked)}
              className="sr-only peer"
              disabled={!hasPermission('manage_settings')}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">API Key</label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            value={settings.integracao?.api_key || ''}
            onChange={(e) => handleInputChange('integracao', 'api_key', e.target.value)}
            placeholder="Insira sua API key"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!hasPermission('manage_settings')}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4 text-gray-400" />
            ) : (
              <Eye className="h-4 w-4 text-gray-400" />
            )}
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Timeout de Requests (segundos)</label>
          <input
            type="number"
            min="5"
            max="120"
            value={settings.integracao?.timeout_requests || 30}
            onChange={(e) => handleInputChange('integracao', 'timeout_requests', parseInt(e.target.value))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!hasPermission('manage_settings')}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Tentativas de Retry</label>
          <input
            type="number"
            min="1"
            max="10"
            value={settings.integracao?.retry_attempts || 3}
            onChange={(e) => handleInputChange('integracao', 'retry_attempts', parseInt(e.target.value))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!hasPermission('manage_settings')}
          />
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'geral':
        return renderGeralTab();
      case 'notificacoes':
        return renderNotificacoesTab();
      case 'seguranca':
        return renderSegurancaTab();
      case 'sistema':
        return renderSistemaTab();
      case 'integracao':
        return renderIntegracaoTab();
      default:
        return renderGeralTab();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configurações do Sistema</h1>
          <p className="text-gray-600">Gerencie as configurações e preferências do sistema</p>
          {lastUpdate && (
            <p className="text-sm text-gray-500 mt-1">
              Última atualização: {lastUpdate.toLocaleTimeString()}
            </p>
          )}
        </div>
        <button
          onClick={handleRefresh}
          className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2"
        >
          <RefreshCw className="h-4 w-4" />
          <span>Atualizar</span>
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center">
          <AlertTriangle className="h-5 w-5 mr-2" />
          {error}
          <button 
            onClick={() => setError('')}
            className="ml-auto text-red-700 hover:text-red-900"
          >
            ×
          </button>
        </div>
      )}

      {message && (
        <div className={`border px-4 py-3 rounded mb-4 flex items-center ${
          message.type === 'success' 
            ? 'bg-green-100 border-green-400 text-green-700' 
            : 'bg-red-100 border-red-400 text-red-700'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5 mr-2" />
          ) : (
            <AlertTriangle className="h-5 w-5 mr-2" />
          )}
          {message.text}
          <button 
            onClick={() => setMessage(null)}
            className="ml-auto hover:opacity-75"
          >
            ×
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {renderTabContent()}
        </div>

        {/* Save Button */}
        {hasPermission('manage_settings') && (
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-lg">
            <div className="flex justify-end">
              <button
                onClick={handleSaveSection}
                disabled={saving}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span>{saving ? 'Salvando...' : 'Salvar Configurações'}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;

